import { useCallback } from 'react';
import { TextInstance, TextAlignment } from '../../services/api';
import SelectDropdown, { SelectOption } from '../../components/SelectDropdown';
import Checkbox from '../../components/Checkbox';
import './TextPanel.css';

/**
 * Text tool settings for creating new texts
 */
export interface TextToolSettings {
  fontFamily: string;
  fontWeight: number;
  color: string;
  alignment: TextAlignment;
  strokeColor: string | null;
  strokeWidth: number;
  fixedSize: boolean;
}

interface TextPanelProps {
  settings: TextToolSettings;
  onSettingsChange: (settings: TextToolSettings) => void;
  selectedTextInstance: TextInstance | null;
  selectedCount?: number;
  onUpdateText: (updates: Partial<TextInstance>) => void;
  onDeleteText: () => void;
}

/**
 * Available font families organized by category
 */
const FONT_FAMILIES: SelectOption[] = [
  // Sans-serif fonts
  { value: 'Arial', label: 'Arial', style: { fontFamily: 'Arial' } },
  { value: 'Arial Black', label: 'Arial Black', style: { fontFamily: 'Arial Black' } },
  { value: 'Helvetica', label: 'Helvetica', style: { fontFamily: 'Helvetica' } },
  { value: 'Verdana', label: 'Verdana', style: { fontFamily: 'Verdana' } },
  { value: 'Tahoma', label: 'Tahoma', style: { fontFamily: 'Tahoma' } },
  { value: 'Trebuchet MS', label: 'Trebuchet MS', style: { fontFamily: 'Trebuchet MS' } },
  { value: 'Lucida Sans', label: 'Lucida Sans', style: { fontFamily: 'Lucida Sans' } },
  { value: 'Segoe UI', label: 'Segoe UI', style: { fontFamily: 'Segoe UI' } },
  { value: 'Calibri', label: 'Calibri', style: { fontFamily: 'Calibri' } },
  // Serif fonts
  { value: 'Times New Roman', label: 'Times New Roman', style: { fontFamily: 'Times New Roman' } },
  { value: 'Georgia', label: 'Georgia', style: { fontFamily: 'Georgia' } },
  { value: 'Palatino Linotype', label: 'Palatino', style: { fontFamily: 'Palatino Linotype' } },
  { value: 'Book Antiqua', label: 'Book Antiqua', style: { fontFamily: 'Book Antiqua' } },
  { value: 'Garamond', label: 'Garamond', style: { fontFamily: 'Garamond' } },
  { value: 'Cambria', label: 'Cambria', style: { fontFamily: 'Cambria' } },
  // Display fonts
  { value: 'Impact', label: 'Impact', style: { fontFamily: 'Impact' } },
  { value: 'Franklin Gothic Medium', label: 'Franklin Gothic', style: { fontFamily: 'Franklin Gothic Medium' } },
  { value: 'Copperplate', label: 'Copperplate', style: { fontFamily: 'Copperplate' } },
  // Monospace fonts
  { value: 'Courier New', label: 'Courier New', style: { fontFamily: 'Courier New' } },
  { value: 'Lucida Console', label: 'Lucida Console', style: { fontFamily: 'Lucida Console' } },
  { value: 'Consolas', label: 'Consolas', style: { fontFamily: 'Consolas' } },
  // Fun fonts
  { value: 'Comic Sans MS', label: 'Comic Sans MS', style: { fontFamily: 'Comic Sans MS' } },
  { value: 'Papyrus', label: 'Papyrus', style: { fontFamily: 'Papyrus' } },
  { value: 'Brush Script MT', label: 'Brush Script', style: { fontFamily: 'Brush Script MT' } }
];

/**
 * Font weight options
 */
const FONT_WEIGHTS: SelectOption[] = [
  { value: 100, label: 'Thin' },
  { value: 200, label: 'Extra Light' },
  { value: 300, label: 'Light' },
  { value: 400, label: 'Normal' },
  { value: 500, label: 'Medium' },
  { value: 600, label: 'Semi Bold' },
  { value: 700, label: 'Bold' },
  { value: 800, label: 'Extra Bold' },
  { value: 900, label: 'Black' }
];

/**
 * Panel for text tool settings and selected text editing
 */
function TextPanel({
  settings,
  onSettingsChange,
  selectedTextInstance,
  selectedCount = 0,
  onUpdateText,
  onDeleteText
}: TextPanelProps): JSX.Element {

  /**
   * Handle setting change (for new text defaults)
   */
  const handleSettingChange = useCallback(<K extends keyof TextToolSettings>(
    key: K,
    value: TextToolSettings[K]
  ) => {
    onSettingsChange({ ...settings, [key]: value });
  }, [settings, onSettingsChange]);

  /**
   * Handle selected text update
   */
  const handleTextUpdate = useCallback(<K extends keyof TextInstance>(
    key: K,
    value: TextInstance[K]
  ) => {
    onUpdateText({ [key]: value });
  }, [onUpdateText]);

  /**
   * Render font controls (used for both new text settings and selected text editing)
   */
  const renderFontControls = (
    currentFont: string,
    currentWeight: number | string,
    currentColor: string,
    currentAlignment: TextAlignment,
    currentStrokeColor: string | null,
    currentStrokeWidth: number,
    currentFixedSize: boolean,
    isSelected: boolean
  ) => (
    <>
      {/* Font Family */}
      <div className="text-panel-control">
        <label className="text-panel-label">Font</label>
        <SelectDropdown
          options={FONT_FAMILIES}
          value={currentFont}
          onChange={(val) => isSelected
            ? handleTextUpdate('fontFamily', val as string)
            : handleSettingChange('fontFamily', val as string)
          }
          previewStyle
        />
      </div>

      {/* Font Weight */}
      <div className="text-panel-control">
        <label className="text-panel-label">Weight</label>
        <SelectDropdown
          options={FONT_WEIGHTS}
          value={typeof currentWeight === 'number' ? currentWeight : 400}
          onChange={(val) => isSelected
            ? handleTextUpdate('fontWeight', val as number)
            : handleSettingChange('fontWeight', val as number)
          }
        />
      </div>

      {/* Text Color */}
      <div className="text-panel-control">
        <label className="text-panel-label">Color</label>
        <div className="text-panel-color-row">
          <input
            type="color"
            className="text-panel-color"
            value={currentColor}
            onChange={(e) => isSelected
              ? handleTextUpdate('color', e.target.value)
              : handleSettingChange('color', e.target.value)
            }
          />
          <input
            type="text"
            className="text-panel-color-input"
            value={currentColor}
            onChange={(e) => isSelected
              ? handleTextUpdate('color', e.target.value)
              : handleSettingChange('color', e.target.value)
            }
          />
        </div>
      </div>

      {/* Alignment */}
      <div className="text-panel-control">
        <label className="text-panel-label">Align</label>
        <div className="text-panel-alignment">
          {(['left', 'center', 'right'] as TextAlignment[]).map((align) => (
            <button
              key={align}
              className={`text-panel-align-btn ${currentAlignment === align ? 'text-panel-align-btn--active' : ''}`}
              onClick={() => isSelected
                ? handleTextUpdate('alignment', align)
                : handleSettingChange('alignment', align)
              }
              title={align.charAt(0).toUpperCase() + align.slice(1)}
            >
              <i className={`fas fa-align-${align}`}></i>
            </button>
          ))}
        </div>
      </div>

      {/* Stroke Color */}
      <div className="text-panel-control">
        <label className="text-panel-label">Stroke</label>
        <div className="text-panel-color-row">
          <Checkbox
            label=""
            checked={currentStrokeColor !== null}
            onChange={(checked) => {
              const val = checked ? '#000000' : null;
              isSelected
                ? handleTextUpdate('strokeColor', val)
                : handleSettingChange('strokeColor', val);
            }}
          />
          {currentStrokeColor !== null && (
            <>
              <input
                type="color"
                className="text-panel-color"
                value={currentStrokeColor}
                onChange={(e) => isSelected
                  ? handleTextUpdate('strokeColor', e.target.value)
                  : handleSettingChange('strokeColor', e.target.value)
                }
              />
              <input
                type="number"
                className="text-panel-stroke-width"
                value={currentStrokeWidth}
                onChange={(e) => {
                  const val = Math.max(0, Math.min(20, parseFloat(e.target.value) || 0));
                  isSelected
                    ? handleTextUpdate('strokeWidth', val)
                    : handleSettingChange('strokeWidth', val);
                }}
                min={0}
                max={20}
                step={0.5}
              />
            </>
          )}
        </div>
      </div>

      {/* Fixed Size */}
      <div className="text-panel-control">
        <label className="text-panel-label">Fixed</label>
        <Checkbox
          label="Size ignores zoom"
          checked={currentFixedSize}
          onChange={(checked) => {
            isSelected
              ? handleTextUpdate('fixedSize', checked)
              : handleSettingChange('fixedSize', checked);
          }}
        />
      </div>
    </>
  );

  return (
    <div className="text-panel">
      {/* Selected Text Instance Controls */}
      {selectedTextInstance && (
        <div className="text-panel-section text-panel-selected">
          <div className="text-panel-section-title">
            {selectedCount > 1 ? `${selectedCount} Texts Selected` : 'Selected Text'}
            <button
              className="text-panel-deselect"
              onClick={() => onUpdateText({})}
              title="Deselect"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>

          {/* Font controls for selected text */}
          <div className="text-panel-controls">
            {renderFontControls(
              selectedTextInstance.fontFamily,
              selectedTextInstance.fontWeight,
              selectedTextInstance.color,
              selectedTextInstance.alignment,
              selectedTextInstance.strokeColor,
              selectedTextInstance.strokeWidth,
              selectedTextInstance.fixedSize ?? false,
              true
            )}
          </div>

          {/* Actions */}
          <div className="text-panel-actions">
            <button
              className="text-panel-action-btn text-panel-action-btn--danger"
              onClick={onDeleteText}
              title={selectedCount > 1 ? `Delete ${selectedCount} Texts` : 'Delete Text'}
            >
              <i className="fas fa-trash"></i>
              <span>Delete</span>
            </button>
          </div>
        </div>
      )}

      {/* New Text Settings */}
      {!selectedTextInstance && (
        <div className="text-panel-section">
          <div className="text-panel-section-title">
            <span>Text Settings</span>
          </div>
          <div className="text-panel-controls">
            {renderFontControls(
              settings.fontFamily,
              settings.fontWeight,
              settings.color,
              settings.alignment,
              settings.strokeColor,
              settings.strokeWidth,
              settings.fixedSize,
              false
            )}
          </div>
        </div>
      )}

      {/* Help Text */}
      <div className="text-panel-help">
        <div className="text-panel-help-item">
          <kbd>Click</kbd> Place text
        </div>
        <div className="text-panel-help-item">
          <kbd>Double-click</kbd> Edit text content
        </div>
        <div className="text-panel-help-item">
          <kbd>Drag handles</kbd> Resize text box
        </div>
        <div className="text-panel-help-item">
          <kbd>Rotate handle</kbd> Rotate text
        </div>
        <div className="text-panel-help-item">
          <kbd>Ctrl + C</kbd> Copy selected
        </div>
        <div className="text-panel-help-item">
          <kbd>Ctrl + X</kbd> Cut selected
        </div>
        <div className="text-panel-help-item">
          <kbd>Ctrl + V</kbd> Paste at cursor
        </div>
        <div className="text-panel-help-item">
          <kbd>Shift + Ctrl + V</kbd> Paste in place
        </div>
        <div className="text-panel-help-item">
          <kbd>Del</kbd> Delete selected
        </div>
      </div>
    </div>
  );
}

export default TextPanel;
